import { api } from '@/lib/api';

export type FindingStatus = 'open' | 'in_progress' | 'resolved';

export const FINDING_STATUS_LABEL: Record<FindingStatus, string> = {
  open: 'Terbuka',
  in_progress: 'Ditindaklanjuti',
  resolved: 'Selesai',
};

/** Dua jenis risalah — inilah yang menentukan separuh isian form. */
export type FindingKind = 'nonconformity' | 'success';

export const FINDING_KIND_LABEL: Record<FindingKind, string> = {
  nonconformity: 'Ketidaksesuaian',
  success: 'Keberhasilan',
};

export type SubjectCategory = 'pelita' | 'tbm';

export const SUBJECT_CATEGORY_LABEL: Record<SubjectCategory, string> = {
  pelita: 'Perkembangan PELITA SLI',
  tbm: 'Perkembangan TBM',
};

/**
 * Label yang berubah menurut jenis risalah.
 *
 * Kolomnya sama di database; yang berbeda cuma pertanyaannya. Ketidaksesuaian
 * bertanya kenapa ini gagal, keberhasilan bertanya kenapa ini berhasil.
 */
export const KIND_LABELS: Record<FindingKind, { rootCause: string; evidence: string }> = {
  nonconformity: { rootCause: 'Akar Masalah', evidence: 'Bukti Penyelesaian' },
  success: { rootCause: 'Faktor Pendukung', evidence: 'Bukti Keberhasilan' },
};

export interface Finding {
  id: string;
  code: string;
  kind: FindingKind;
  regionId: string;
  regionName: string;
  periodName: string;
  foundDate: string | null;
  subjectCategory: SubjectCategory | null;
  weakIndicator: string | null;
  description: string;
  dueDate: string | null;
  assessorName: string | null;
  status: FindingStatus;
  resolvedAt: string | null;
  updatedAt: string;
}

export interface FindingLog {
  id: string;
  fromStatus: string | null;
  toStatus: string | null;
  comment: string | null;
  createdAt: string;
  byName: string | null;
}

export interface FindingDetail extends Finding {
  periodId: string;
  rootCause: string | null;
  correction: string | null;
  correctiveAction: string | null;
  evidenceNote: string | null;
  assessorId: string | null;
  logs: FindingLog[];
}

export interface FindingInput {
  kind: FindingKind;
  periodId: string;
  regionId: string;
  foundDate: string | null;
  weakIndicator: string | null;
  subjectCategory: SubjectCategory | null;
  description: string;
  rootCause: string | null;
  correction: string | null;
  correctiveAction: string | null;
  dueDate: string | null;
  evidenceNote: string | null;
  assessorId: string | null;
}

export interface Reflection {
  id: string;
  tbmId: string;
  tbmCode: string;
  tbmName: string;
  programName: string;
  observedDate: string | null;
  status: 'draft' | 'final';
  updatedAt: string;
}

export interface ReflectionDetail extends Reflection {
  periodId: string;
  purpose: string | null;
  achievements: string | null;
  challenges: string | null;
  rootCause: string | null;
  nextActions: string | null;
  lessons: string | null;
}

export interface ReflectionInput {
  tbmId: string;
  periodId: string;
  observedDate: string | null;
  programName: string;
  purpose: string | null;
  achievements: string | null;
  challenges: string | null;
  rootCause: string | null;
  nextActions: string | null;
  lessons: string | null;
  status?: 'draft' | 'final';
}

/** Tiga kategori bukti pada pertanyaan 4 lembar Refleksi. */
export const EVIDENCE_CATEGORIES = [
  { key: 'reflection_pre', label: 'Dokumen Pra Program' },
  { key: 'reflection_exec', label: 'Dokumen Pelaksanaan Program' },
  { key: 'reflection_impact', label: 'Dokumen Pengukuran Dampak' },
] as const;

export const monevApi = {
  listFindings: (params?: { periodId?: string; status?: string; kind?: string }) => {
    const search = new URLSearchParams();
    if (params?.periodId) search.set('periodId', params.periodId);
    if (params?.status) search.set('status', params.status);
    if (params?.kind) search.set('kind', params.kind);
    return api.get<Finding[]>(`/monev/findings?${search}`);
  },
  getFinding: (id: string) => api.get<FindingDetail>(`/monev/findings/${id}`),
  createFinding: (body: FindingInput) =>
    api.post<{ id: string; code: string }>('/monev/findings', body),
  updateFinding: (id: string, body: Partial<FindingInput>) =>
    api.patch(`/monev/findings/${id}`, body),
  setFindingStatus: (id: string, status: FindingStatus, comment?: string) =>
    api.post(`/monev/findings/${id}/status`, { status, comment: comment ?? null }),
  deleteFinding: (id: string) => api.del(`/monev/findings/${id}`),

  listReflections: (periodId?: string) =>
    api.get<Reflection[]>(`/monev/reflections?${periodId ? `periodId=${periodId}` : ''}`),
  getReflection: (id: string) => api.get<ReflectionDetail>(`/monev/reflections/${id}`),
  createReflection: (body: ReflectionInput) =>
    api.post<{ id: string }>('/monev/reflections', body),
  updateReflection: (id: string, body: Partial<ReflectionInput>) =>
    api.patch(`/monev/reflections/${id}`, body),
  deleteReflection: (id: string) => api.del(`/monev/reflections/${id}`),
};
